import React, { useState } from "react";
import {
  ArrowLeft,
  Upload,
  Sparkles,
  Download,
  ShoppingCart,
  Palette,
  Wand2,
  Loader2,
  X,
  AlertCircle,
} from "lucide-react";

const ImageUploadTransform = ({ onBack }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("Impressionist");
  const [customStyle, setCustomStyle] = useState("");
  const [isTransforming, setIsTransforming] = useState(false);
  const [transformedImage, setTransformedImage] = useState(null);
  const [showWatermark, setShowWatermark] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  const paintingStyles = [
    "Impressionist",
    "Realism",
    "Watercolor",
    "Cyberpunk",
    "Oil on Canvas",
    "Charcoal",
    "Surrealism",
    "Pop Art",
    "Art Nouveau",
    "Cartoon",
    "Custom",
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

  const validateFile = (file) => {
    if (!file) return "No file selected";

    if (!allowedTypes.includes(file.type)) {
      return "Invalid file type. Please upload JPEG, PNG, or WebP images only.";
    }

    if (file.size > maxFileSize) {
      return "File size too large. Maximum size is 10MB.";
    }

    return null;
  };

  const handleFileSelect = (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Auto-generate description from filename
    const filename = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
    const cleanName = filename
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    setDescription(cleanName || "Uploaded image");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setTransformedImage(null);
    setError("");
    setDescription("");
  };

  const handleTransform = async () => {
    if (!selectedFile || !selectedStyle) {
      setError("Please select an image and painting style");
      return;
    }

    setIsTransforming(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("style", selectedStyle);
      formData.append("description", description || "Uploaded image");

      if (selectedStyle === "Custom" && customStyle.trim()) {
        formData.append("customStyle", customStyle.trim());
      }

      const response = await fetch("/api/images/upload-transform", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setTransformedImage({
          url: data.imageUrl,
          title: data.title || "Uploaded",
          style: selectedStyle === "Custom" ? customStyle : selectedStyle,
          orientation: "square",
          id: data.imageId,
        });
        setShowWatermark(true);
      } else {
        setError(
          "Failed to transform image: " + (data.error || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Transform error:", error);
      setError("Error transforming image. Please try again.");
    } finally {
      setIsTransforming(false);
    }
  };

  const handleDownload = async () => {
    if (!transformedImage?.id) return;

    try {
      const response = await fetch(
        `/api/images/${transformedImage.id}/download`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      const data = await response.json();

      if (data.success) {
        // Create download link
        const link = document.createElement("a");
        link.href = data.downloadUrl;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("Download failed: " + data.error);
      }
    } catch (error) {
      console.error("Download error:", error);
      alert("Download failed. Please try again.");
    }
  };

  const handleOrder = () => {
    console.log("Order image:", transformedImage?.id);
    // Order flow will be implemented
    alert("Order functionality coming soon!");
  };

  const WatermarkedImage = ({ src, alt }) => (
    <div className="relative group">
      <img
        src={src}
        alt={alt}
        className="w-full h-auto rounded-xl shadow-2xl max-h-96 object-contain"
        onLoad={(e) => console.log("Image loaded successfully:", e.target.src)}
        onError={(e) => console.error("Image failed to load:", e.target.src)}
      />
      {/* ✅ REMOVE CLIENT-SIDE WATERMARK since server already has it */}
      {/* {showWatermark && (
      <div className="absolute inset-0 pointer-events-none">
        // ... watermark overlay code
      </div>
    )} */}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <nav className="px-8 py-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 text-white/80 hover:text-white transition-colors duration-200 hover:bg-white/10 rounded-lg"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center space-x-2">
                <Palette className="text-purple-400" size={28} />
                <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  canvasGen.ai
                </span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white">
              Upload & Transform
            </h1>
          </div>
        </nav>

        <div className="px-8 pb-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Left Side - Upload & Controls */}
            <div className="space-y-8">
              {/* File Upload Area */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h2 className="text-2xl font-bold text-white mb-4">
                  Upload Your Image
                </h2>

                {!selectedFile ? (
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
                      dragActive
                        ? "border-purple-400 bg-purple-500/20"
                        : "border-white/30 hover:border-white/50 hover:bg-white/5"
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    <Upload size={48} className="mx-auto text-white/60 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">
                      Drag & drop your image here
                    </h3>
                    <p className="text-white/70 mb-4">
                      or click to browse files
                    </p>
                    <p className="text-white/50 text-sm">
                      Supports: JPEG, PNG, WebP (max 10MB)
                    </p>
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* File Preview */}
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Upload preview"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <button
                        onClick={removeFile}
                        className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* File Info */}
                    <div className="text-white/80 text-sm space-y-1">
                      <div>
                        <strong>File:</strong> {selectedFile.name}
                      </div>
                      <div>
                        <strong>Size:</strong>{" "}
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                      <div>
                        <strong>Type:</strong> {selectedFile.type}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start space-x-2">
                    <AlertCircle
                      size={20}
                      className="text-red-400 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-red-200 text-sm">{error}</p>
                  </div>
                )}
              </div>

              {/* Description Input */}
              {selectedFile && (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Describe Your Image
                  </h3>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="A beautiful landscape photo, a portrait of a person, an abstract composition..."
                    className="w-full h-24 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    maxLength={200}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-white/60 text-sm">
                      {description.length}/200 characters
                    </span>
                    <span className="text-white/60 text-sm">
                      Title: "Uploaded"
                    </span>
                  </div>
                </div>
              )}

              {/* Style Selection */}
              {selectedFile && (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Select Painting Style
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {paintingStyles.map((style) => (
                      <button
                        key={style}
                        onClick={() => setSelectedStyle(style)}
                        className={`p-3 rounded-lg border transition-all duration-200 text-sm font-medium ${
                          selectedStyle === style
                            ? "border-purple-500 bg-purple-500/20 text-white"
                            : "border-white/20 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>

                  {/* Custom Style Input */}
                  {selectedStyle === "Custom" && (
                    <div className="mt-4">
                      <input
                        type="text"
                        value={customStyle}
                        onChange={(e) => setCustomStyle(e.target.value)}
                        placeholder="Enter your custom style (e.g., 'Van Gogh inspired with swirling brushstrokes')"
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Transform Button */}
              {selectedFile && (
                <button
                  onClick={handleTransform}
                  disabled={
                    isTransforming ||
                    !selectedStyle ||
                    (selectedStyle === "Custom" && !customStyle.trim())
                  }
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 shadow-lg flex items-center justify-center space-x-2"
                >
                  {isTransforming ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Transforming...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 size={20} />
                      <span>Transform Image</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Right Side - Preview */}
            <div className="space-y-6">
              {isTransforming ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Transforming Your Image...
                  </h3>

                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/50 to-blue-900/50 aspect-square flex items-center justify-center">
                    <div className="text-center">
                      <div className="relative mb-6">
                        {/* Magic Wand Animation */}
                        <div className="text-6xl animate-bounce mb-4">🪄</div>
                        <div className="text-4xl animate-pulse">✨</div>
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-2xl font-bold text-white animate-pulse">
                          Applying {selectedStyle} style...
                        </h4>
                        <p className="text-white/80 animate-pulse">
                          AI is analyzing and transforming your image
                        </p>

                        <div className="flex justify-center space-x-1 mt-4">
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                          <div
                            className="w-2 h-2 bg-white rounded-full animate-bounce"
                            style={{ animationDelay: "0.1s" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-white rounded-full animate-bounce"
                            style={{ animationDelay: "0.2s" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-200 text-sm text-center">
                      <Wand2 size={16} className="inline mr-1" />
                      This may take a few moments depending on image
                      complexity...
                    </p>
                  </div>
                </div>
              ) : transformedImage ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Your Transformed Artwork
                  </h3>

                  <WatermarkedImage
                    src={transformedImage.url}
                    alt={transformedImage.title}
                  />

                  {/* Image Info */}
                  <div className="mt-4 space-y-2 text-white/80">
                    <div>
                      <strong>Title:</strong> {transformedImage.title}
                    </div>
                    <div>
                      <strong>Style:</strong> {transformedImage.style}
                    </div>
                    <div>
                      <strong>Type:</strong> Transformed Upload
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={handleDownload}
                      className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <Download size={18} />
                      <span>Download</span>
                    </button>
                    <button
                      onClick={handleOrder}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <ShoppingCart size={18} />
                      <span>Order Print</span>
                    </button>
                  </div>

                  {/* Watermark Notice */}
                  <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-sm">
                      <Sparkles size={16} className="inline mr-1" />
                      Watermark will be removed when you order or download your
                      artwork
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12 border border-white/20 border-dashed text-center">
                  <Upload size={48} className="mx-auto text-white/40 mb-4" />
                  <h3 className="text-xl font-bold text-white/60 mb-2">
                    Upload an Image to Get Started
                  </h3>
                  <p className="text-white/40">
                    Upload your image, choose a painting style, and watch AI
                    transform it into beautiful artwork
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadTransform;
